import { AreaSwitcher, Box, ColumnSelector, InputWrapper, Select, TextArea, TextInput } from "@bcc-ui/ui-components";
import { Form, Formik, FormikProps, useFormikContext } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import {
    AssetViewFilters,
    getFilterError,
    isConditionGroup,
} from "components/asset-management-system/AssetViewFilters";
import CustomDatePickerView from "components/common-date-picker/CustomDatePicker";
import { FilterOperator } from "domain/asset-management/assetFilterConstants";
import { AssetPathKey } from "domain/asset-management/assetPathMapping";
import {
    ASSET_OVERVIEW,
    AssetCustomView,
    AssetCustomViewConditionGroup,
    AssetCustomViewFilterEntry,
    isEndDateEntry,
    isStartDateEntry,
    withDateRangeFilters,
} from "domain/asset-management/assets";
import { NOTES_MAX_LENGTH } from "domain/globalConstants";
import { assetService } from "services/asset-management/AssetService";
import { createTranslatorPath } from "services/pathTranslator";
import { Category } from "services/statistics/UsageStatisticsService";

const MINIMUM_NAME_LENGTH = 2;
const MAXIMUM_NAME_LENGTH = 32;

const DEFAULT_COLUMNS = [...new Set(ASSET_OVERVIEW)];

const mergeDateRangeIntoFilters = (
    filters: AssetCustomViewFilterEntry[],
    dateRange?: [string, string]
): AssetCustomViewFilterEntry[] => withDateRangeFilters(filters, dateRange?.[0], dateRange?.[1]);

export interface AssetCustomViewFormValues {
    name: string;
    private: boolean;
    columns: string[];
    filters: AssetCustomViewFilterEntry[];
    parentFolder: string | undefined;
    description: string;
    dateRange?: [string, string];
}

interface Props {
    formId: string;
    onSubmit: (values: AssetCustomViewFormValues) => void;
    initialValues?: AssetCustomViewFormValues;
    folder: boolean;
    existingNames: string[];
    isDuplicate?: boolean;
    onSubmitDisabledChange?: (disabled: boolean) => void;
    folders?: AssetCustomView[];
    isFilterTabSelected?: boolean;
}

const ManageAssetCustomViewForm = (props: Props) => {
    const { t } = useTranslation();
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");
    const [isColumnTabSelected, setIsColumnTabSelected] = React.useState(
        props.isFilterTabSelected !== undefined ? !props.isFilterTabSelected : true
    );
    const [availableColumns, setAvailableColumns] = React.useState<
        { value: string; label?: string; required?: boolean }[]
    >([]);
    const [submittedOnce, setSubmittedOnce] = React.useState(false);
    const [formHasErrors, setFormHasErrors] = React.useState(false);
    const [parentView, setParentView] = React.useState<string | undefined>();

    React.useEffect(() => {
        setParentView(props.folders?.find((folder) => folder.uuid === props.initialValues?.parentFolder)?.uuid);
    }, [props.folders, props.initialValues]);

    React.useEffect(() => {
        props.onSubmitDisabledChange?.(formHasErrors);
    }, [formHasErrors]);

    React.useEffect(() => {
        const abortController = new AbortController();
        assetService
            .fetchExistingKeys(abortController)
            .then((response) => {
                const allKnownKeys = Object.values(AssetPathKey);
                const customColumns = response.keys
                    .filter((key) => !allKnownKeys.includes(key as AssetPathKey))
                    .map((key) => ({ value: key, label: key, required: false }));
                const knownColumns: { value: string; label: string; required: boolean }[] = allKnownKeys.map((key) => ({
                    value: key,
                    label: translatedPaths(key),
                    required: key === AssetPathKey.ASSET_ID,
                }));
                const columns = knownColumns.concat(customColumns).sort((a, b) => a.label.localeCompare(b.label));
                setAvailableColumns(columns);
            })
            .catch(() => {
                setAvailableColumns(
                    DEFAULT_COLUMNS.map((key) => ({
                        value: key,
                        label: translatedPaths(key),
                        required: key === AssetPathKey.ASSET_ID,
                    }))
                );
            });
        return () => abortController.abort();
    }, []);

    const assetFilterConditionSchema = Yup.object().shape({
        field: Yup.string().required(t("AssetManagement.filters.selectField")),
        value: Yup.string().when("operator", {
            is: (operator: string) => operator !== FilterOperator.EXIST,
            then: (schema: Yup.StringSchema) =>
                schema.required(t("AssetManagement.createAssetSubpage.fieldValueErrorMessage")),
        }),
    });

    const assetFilterGroupConditionSchema = Yup.object().shape({
        conditions: Yup.array().of(assetFilterConditionSchema),
    });

    const assetFilterEntrySchema = Yup.lazy((entry) =>
        Array.isArray((entry as AssetCustomViewConditionGroup)?.conditions)
            ? assetFilterGroupConditionSchema
            : assetFilterConditionSchema
    );

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .required(
                t("AssetManagement.createAssetCustomView.nameMinimumLength", { minimumLength: MINIMUM_NAME_LENGTH })
            )
            .min(
                MINIMUM_NAME_LENGTH,
                t("AssetManagement.createAssetCustomView.nameMinimumLength", { minimumLength: MINIMUM_NAME_LENGTH })
            )
            .max(
                MAXIMUM_NAME_LENGTH,
                t("AssetManagement.createAssetCustomView.nameMaximumLength", { maximumLength: MAXIMUM_NAME_LENGTH })
            )
            .test({
                name: "test-asset-group-name",
                test: function (value) {
                    if (!value) {
                        return true;
                    }

                    const trimmedName = value.trim();
                    const isOwnName = !props.isDuplicate && trimmedName === props.initialValues?.name;
                    if (!isOwnName && props.existingNames.includes(trimmedName)) {
                        return this.createError({
                            message: t("AssetManagement.createAssetCustomView.duplicateNameError", {
                                name: trimmedName,
                            }),
                        });
                    }

                    return true;
                },
            }),
        columns: Yup.array().min(1),
        filters: Yup.array(assetFilterEntrySchema),
    });

    return (
        <Formik
            initialValues={
                props.initialValues ?? {
                    name: "",
                    private: true,
                    columns: DEFAULT_COLUMNS,
                    filters: [],
                    parentFolder: undefined,
                    description: "",
                    dateRange: undefined,
                }
            }
            enableReinitialize
            onSubmit={(values) => {
                props.onSubmit({ ...values, name: values.name.trim() });
            }}
            validationSchema={validationSchema}
            validateOnChange={submittedOnce}
        >
            {({
                errors,
                handleChange,
                setFieldValue,
                values,
                dirty,
                touched,
            }: FormikProps<AssetCustomViewFormValues>) => {
                const unsortedDefaultColumns = values.columns.map(
                    (key) =>
                        availableColumns.find((col) => col.value === key) ?? {
                            value: key,
                            label: translatedPaths(key),
                            required: key === AssetPathKey.ASSET_ID,
                        }
                );
                const defaultColumns = [
                    ...unsortedDefaultColumns.filter((column) => column.value === AssetPathKey.ASSET_ID),
                    ...unsortedDefaultColumns.filter((column) => column.value !== AssetPathKey.ASSET_ID),
                ];

                return (
                    <Form id={props.formId}>
                        <FormValidityTracker setFormHasErrors={setFormHasErrors} setSubmittedOnce={setSubmittedOnce} />
                        <TextInput
                            label={t("Common.nameWithColon")}
                            name={"name"}
                            id={"name"}
                            value={values.name}
                            onChange={handleChange}
                            autoFocus
                            error={errors.name}
                        />
                        {!props.folder && (
                            <>
                                <Box columnLayout paddingTop={"Md"}>
                                    <Box paddingBottom={"Md"}>
                                        <Select
                                            key={parentView}
                                            name={"parentFolder"}
                                            label={t("AssetManagement.createAssetCustomView.addToFolder")}
                                            items={props.folders?.map((folder) => ({
                                                value: folder.uuid,
                                                label: folder.name,
                                            }))}
                                            placeholder={t("Common.select")}
                                            selectedByDefault={parentView}
                                            dropdownMaxHeight={200}
                                            onSelection={(value) => setFieldValue("parentFolder", String(value))}
                                        />
                                    </Box>
                                    <TextArea
                                        id={"description"}
                                        label={t("Common.description")}
                                        value={values.description}
                                        onChange={handleChange}
                                        helpText={t("Common.charactersLeft", {
                                            remainingCharacters: NOTES_MAX_LENGTH - values.description.length,
                                            descriptionMaxLength: NOTES_MAX_LENGTH,
                                        })}
                                        maxLength={NOTES_MAX_LENGTH}
                                    />
                                </Box>
                                <Box columnLayout gap={"Sm"} paddingTop={"Lg"} paddingBottom={"Sm"}>
                                    <AreaSwitcher
                                        buttonLeft={{
                                            text: t("CreateReportView.form.columnsTab"),
                                            callback: () => setIsColumnTabSelected(true),
                                            selected: isColumnTabSelected,
                                        }}
                                        buttonRight={{
                                            text: t("CreateReportView.form.filtersTab"),
                                            callback: () => setIsColumnTabSelected(false),
                                            selected: !isColumnTabSelected,
                                        }}
                                    />
                                </Box>
                                {isColumnTabSelected ? (
                                    <ColumnSelector
                                        key={availableColumns.length}
                                        columns={availableColumns}
                                        defaultColumns={defaultColumns}
                                        onColumnSelection={(selected) => setFieldValue("columns", selected)}
                                        showTooltips
                                        columnSelectorTexts={{
                                            placeholderText: t("Common.search"),
                                            availableColumnsText: t("CreateReportView.form.availableColumns"),
                                            selectedColumnsText: t("CreateReportView.form.selectedColumns"),
                                            errorSelectedColumnsEmptyText: t("AssetManagement.columnSelectorEmpty"),
                                        }}
                                        isVirtualized
                                        errorState={{
                                            errorText: t("AssetManagement.columnSelectorEmpty"),
                                            showError: true,
                                        }}
                                    />
                                ) : (
                                    <>
                                        <Box paddingTop={"Xs"} paddingBottom={"Md"}>
                                            <InputWrapper
                                                name={"label"}
                                                label={t("AssetManagement.filters.dateFilter")}
                                            >
                                                <CustomDatePickerView
                                                    defaultPlaceholder={false}
                                                    editable
                                                    defaultDateRange={
                                                        values.dateRange && [
                                                            new Date(values.dateRange[0]),
                                                            new Date(values.dateRange[1]),
                                                        ]
                                                    }
                                                    category={Category.ASSET_VIEW_MANAGEMENT}
                                                    onUpdate={(startDate?: string, endDate?: string) => {
                                                        setFieldValue(
                                                            "filters",
                                                            mergeDateRangeIntoFilters(
                                                                values.filters,
                                                                startDate && endDate ? [startDate, endDate] : undefined
                                                            )
                                                        );
                                                        setFieldValue(
                                                            "dateRange",
                                                            startDate && endDate ? [startDate, endDate] : undefined
                                                        );
                                                    }}
                                                    placement={"bottomStart"}
                                                />
                                            </InputWrapper>
                                        </Box>

                                        <AssetViewFilters
                                            filters={values.filters.filter(
                                                (filter) => !isStartDateEntry(filter) && !isEndDateEntry(filter)
                                            )}
                                            onChange={(filters) =>
                                                setFieldValue(
                                                    "filters",
                                                    mergeDateRangeIntoFilters(filters, values.dateRange)
                                                )
                                            }
                                            dirty={dirty}
                                            touched={touched.filters}
                                            errors={errors}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ManageAssetCustomViewForm;

interface FormValidityTrackerProps {
    setFormHasErrors: (value: boolean) => void;
    setSubmittedOnce: (value: boolean) => void;
}

interface ConditionPath {
    absoluteIndex: number;
    conditionIndex?: number;
}

const FormValidityTracker = (props: FormValidityTrackerProps) => {
    const { errors, touched, values, submitCount } = useFormikContext<AssetCustomViewFormValues>();

    React.useEffect(() => {
        if (submitCount > 0) {
            props.setSubmittedOnce(true);
        }

        const conditionPaths = values.filters.flatMap<ConditionPath>((entry, absoluteIndex) =>
            isConditionGroup(entry)
                ? entry.conditions.map((_, conditionIndex) => ({ absoluteIndex, conditionIndex }))
                : [{ absoluteIndex }]
        );
        const hasVisibleFilterErrors = conditionPaths.some(
            ({ absoluteIndex, conditionIndex }) =>
                getFilterError("field", absoluteIndex, touched.filters, errors.filters, conditionIndex) ||
                getFilterError("value", absoluteIndex, touched.filters, errors.filters, conditionIndex)
        );
        const hasNonFilterErrors = Object.keys(errors).some((key) => key !== "filters");
        props.setFormHasErrors(hasNonFilterErrors || hasVisibleFilterErrors);
    }, [errors, touched, values, submitCount]);

    return null;
};
